import React from 'react';
import styled from 'styled-components';

interface CallNotificationProps {
  callerName: string;
  onAccept: () => void;
  onDecline: () => void;
  isOutgoing?: boolean;
  isDeclined?: boolean;
  isCancelled?: boolean;
  isMissed?: boolean;
}

const NotificationContainer = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  width: 90%;
  max-width: 400px;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CallerAvatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: #7e22ce;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.25rem;
`;

const CallerInfo = styled.div`
  flex: 1;
`;

const CallerName = styled.h4`
  font-size: 1.125rem;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const CallType = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const ActionButton = styled.button<{ variant: 'accept' | 'decline' }>`
  flex: 1;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  background: ${props => props.variant === 'accept' ? '#7e22ce' : '#f3f4f6'};
  color: ${props => props.variant === 'accept' ? 'white' : '#4b5563'};

  &:hover {
    background: ${props => props.variant === 'accept' ? '#6b21a8' : '#e5e7eb'};
    transform: translateY(-1px);
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const CallNotification: React.FC<CallNotificationProps> = ({
  callerName,
  onAccept,
  onDecline,
  isOutgoing = false,
  isDeclined = false,
  isCancelled = false,
  isMissed = false
}) => {
  // Play ringtone for incoming calls
  React.useEffect(() => {
    let audio: HTMLAudioElement | null = null;
    let playPromise: Promise<void> | undefined;

    const playAudio = async (src: string, shouldLoop: boolean) => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      audio = new Audio(src);
      audio.loop = shouldLoop;
      try {
        playPromise = audio.play();
        await playPromise;
      } catch (error) {
        console.log('Audio playback failed:', error);
      }
    };

    if (isOutgoing) {
      playAudio('/sounds/dial.mp3', true);
    } else if (!isDeclined && !isCancelled && !isMissed) {
      playAudio('/sounds/ringtone.mp3', true);
    } else if (isDeclined || isCancelled || isMissed) {
      playAudio('/sounds/beep.mp3', false);
    }

    return () => {
      if (audio) {
        if (playPromise !== undefined) {
          playPromise.then(() => {
            audio?.pause();
            audio!.currentTime = 0;
          }).catch(() => {
            // Ignore errors during cleanup
          });
        } else {
          audio.pause();
          audio.currentTime = 0;
        }
      }
    };
  }, [isOutgoing, isDeclined, isCancelled, isMissed]);

  let message = '';
  if (isOutgoing) message = 'Calling...';
  else if (isDeclined) message = 'Call declined';
  else if (isCancelled) message = 'Call cancelled';
  else if (isMissed) message = 'Missed call';
  else message = 'Incoming voice call';

  return (
    <NotificationContainer>
      <NotificationHeader>
        <CallerAvatar>
          {callerName.charAt(0)}
        </CallerAvatar>
        <CallerInfo>
          <CallerName>{callerName}</CallerName>
          <CallType>{message}</CallType>
        </CallerInfo>
      </NotificationHeader>

      <NotificationActions>
        {/* Accept only for incoming call */}
        {!isOutgoing && !isDeclined && !isCancelled && !isMissed && (
          <ActionButton variant="accept" onClick={onAccept}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            Accept
          </ActionButton>
        )}
        {/* Decline/Cancel always shown */}
        <ActionButton variant="decline" onClick={onDecline}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
            <line x1="23" y1="1" x2="1" y2="23" />
          </svg>
          {isOutgoing ? 'Cancel' : 'Decline'}
        </ActionButton>
      </NotificationActions>
    </NotificationContainer>
  );
};

export default CallNotification; 